// Centralized booking cache to prevent duplicate API calls
import { getBookings } from "../utils/api";

class BookingCache {
  constructor() {
    this.cache = new Map(); // Key: 'YYYY-MM-DD', Value: events array
    this.pendingRequests = new Map(); // Track in-flight requests
    this.listeners = [];
  }

  // Subscribe to cache updates
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  // Notify all listeners of updates
  notify(date, events) {
    this.listeners.forEach((callback) => callback(date, events));
  }

  // Get cache key from date
  getCacheKey(date) {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  }

  // Check if date is cached
  has(date) {
    return this.cache.has(this.getCacheKey(date));
  }

  // Get cached events for a specific date
  get(date) {
    return this.cache.get(this.getCacheKey(date)) || [];
  }

  // Get all cached events
  getAll() {
    const allEvents = [];
    this.cache.forEach((events) => {
      // Ensure events is an array before spreading
      if (Array.isArray(events)) {
        allEvents.push(...events);
      } else if (events) {
        // If events exists but is not an array, log warning and skip
        // console.warn("[BookingCache] Non-array value found in cache:", events);
      }
    });
    return allEvents;
  }

  // Fetch events for a specific date (single day only)
  async fetchDate(resource, date) {
    const key = this.getCacheKey(date);

    // Return cached data if available
    if (this.cache.has(key)) {
      // eslint-disable-next-line no-console
      // console.log("📦 Using cached data for:", key);
      return this.cache.get(key);
    }

    // Return existing pending request if one exists
    if (this.pendingRequests.has(key)) {
      // eslint-disable-next-line no-console
      // console.log("⏳ Waiting for pending request:", key);
      return this.pendingRequests.get(key);
    }

    // eslint-disable-next-line no-console
    // console.log("🌐 Fetching bookings for:", key);

    // Create date range for single day
    const dateFrom = new Date(date);
    dateFrom.setHours(0, 0, 0, 0);

    const dateTo = new Date(date);
    dateTo.setHours(23, 59, 59, 999);

    // Create the request promise
    const requestPromise = getBookings(resource, dateFrom, dateTo)
      .then((data) => {
        if (data && data.events) {
          // Ensure events is an array before caching
          const events = Array.isArray(data.events) ? data.events : [];

          // Cache the events
          this.cache.set(key, events);

          // Notify listeners
          this.notify(date, events);

          return events;
        }
        return [];
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        // console.error("Error fetching bookings:", error);
        return [];
      })
      .finally(() => {
        // Remove from pending requests
        this.pendingRequests.delete(key);
      });

    // Store the pending request
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  // Prefetch events for a range of dates (e.g., entire month)
  async prefetchRange(resource, dateFrom, dateTo) {
    const dates = [];
    const current = new Date(dateFrom);
    const end = new Date(dateTo);

    // eslint-disable-next-line no-unmodified-loop-condition
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    // Fetch only uncached dates
    const uncachedDates = dates.filter((date) => !this.has(date));

    if (uncachedDates.length === 0) {
      // eslint-disable-next-line no-console
      // console.log("📦 All dates already cached");
      return;
    }

    // eslint-disable-next-line no-console
    // console.log(`🌐 Prefetching ${uncachedDates.length} dates`);

    // Fetch all uncached dates in parallel
    await Promise.all(
      uncachedDates.map((date) => this.fetchDate(resource, date))
    );
  }

  // Clear cache (useful when data changes via socket)
  clear() {
    // eslint-disable-next-line no-console
    // console.log("🗑️ Clearing booking cache");
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Clear specific date
  clearDate(date) {
    const key = this.getCacheKey(date);
    // eslint-disable-next-line no-console
    // console.log("🗑️ Clearing cache for:", key);
    this.cache.delete(key);
  }

  // Clear date range
  clearRange(dateFrom, dateTo) {
    const current = new Date(dateFrom);
    const end = new Date(dateTo);
    // eslint-disable-next-line no-unmodified-loop-condition
    while (current <= end) {
      this.clearDate(current);
      current.setDate(current.getDate() + 1);
    }
  }
}

// Export singleton instance
export const bookingCache = new BookingCache();
